import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

export const NEWS_IMAGE_WIDTH = 1080;
export const NEWS_IMAGE_HEIGHT = 1350;
export const NEWS_IMAGE_ASPECT = NEWS_IMAGE_WIDTH / NEWS_IMAGE_HEIGHT;
export const NEWS_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

async function getCroppedFile(image, crop, fileName, mimeType = "image/jpeg") {
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  const canvas = document.createElement("canvas");
  canvas.width = NEWS_IMAGE_WIDTH;
  canvas.height = NEWS_IMAGE_HEIGHT;
  const ctx = canvas.getContext("2d");

  const sourceX = crop.x * scaleX;
  const sourceY = crop.y * scaleY;
  const sourceWidth = crop.width * scaleX;
  const sourceHeight = crop.height * scaleY;

  ctx.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    NEWS_IMAGE_WIDTH,
    NEWS_IMAGE_HEIGHT,
  );

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Crop failed"))),
      mimeType,
      0.92,
    );
  });

  const safeName = fileName?.replace(/\.[^.]+$/, "") || "news-image";
  const ext = mimeType === "image/png" ? "png" : "jpg";
  return new File([blob], `${safeName}.${ext}`, { type: mimeType });
}

/**
 * Fixed-aspect crop modal for news images (1080 x 1350).
 */
const ImageCropModal = ({
  show,
  imageSrc,
  fileName = "news-image.jpg",
  mimeType = "image/jpeg",
  onCancel,
  onConfirm,
}) => {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) {
      setCrop(undefined);
      setCompletedCrop(undefined);
      setSaving(false);
    }
  }, [show, imageSrc]);

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    const initial = centerAspectCrop(width, height, NEWS_IMAGE_ASPECT);
    setCrop(initial);
    setCompletedCrop(initial);
  }, []);

  const handleConfirm = async () => {
    if (!imgRef.current || !completedCrop?.width || !completedCrop?.height) {
      return;
    }
    setSaving(true);
    try {
      // onComplete always provides pixel crop relative to displayed image
      const file = await getCroppedFile(
        imgRef.current,
        completedCrop,
        fileName,
        mimeType.startsWith("image/png") ? "image/png" : "image/jpeg",
      );
      const previewUrl = URL.createObjectURL(file);
      onConfirm(file, previewUrl);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onCancel} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Crop News Image (1080 x 1350)</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted mb-3">
          Adjust the crop area. Image will be saved at {NEWS_IMAGE_WIDTH} x{" "}
          {NEWS_IMAGE_HEIGHT}px.
        </p>
        {imageSrc && (
          <div className="d-flex justify-content-center">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={NEWS_IMAGE_ASPECT}
              minWidth={40}
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Crop"
                onLoad={onImageLoad}
                style={{ maxHeight: "60vh", maxWidth: "100%" }}
              />
            </ReactCrop>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-primary iclose_btn"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-success isubmit_button"
          onClick={handleConfirm}
          disabled={saving || !completedCrop?.width}
        >
          {saving ? "Saving..." : "Apply Crop"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImageCropModal;
